import { type ExpandedResolvedWishlistEntryModel } from "~/Wishlist/model";
import { type Table } from "@tanstack/react-table";
import { IMG_EMPTY_WISHLIST } from "~/utils/url/staticResources";

import { useCallback } from "react";
import { useListVisibilityWatcher } from "~/common/hooks/useListVisibilityWatcher";
import { useDispatch } from "react-redux";
import { wishlistAfterScroll } from "~/Wishlist/redux/actions";
import { useWishlistQuickEntry } from "~/Wishlist/page/WishlistPage/useWishlistQuickEntry";
import { Icon } from "~/common/components/icon/Icon";
import { IconType } from "~/common/components/icon/IconType";
import Checkbox from "~/common/components/Checkbox";

import classes from "./WishlistDetails.module.less";
import { WishlistItem } from "~/Wishlist/page/WishlistPage/components/WishlistDetails/components/WishlistItem/WishlistItem";
import { LinkButton } from "~/common/components/Link";
import classNames from "classnames";
import { InformationPage } from "~/common/components/informationPage/InformationPage";
import { userIsRegistered } from "~/utils/roles";
import { useTranslation } from "react-i18next";
import { canSeeAddToCartButton } from "~/utils/features";

interface Props {
  tableRef: Table<ExpandedResolvedWishlistEntryModel>;
  isCustomer: boolean;
  className?: string;
  makeSetSaleEnabled: (index: number) => (saleEnabled: boolean) => void;
}
export const WishlistDetails = ({ tableRef, isCustomer, className, makeSetSaleEnabled }: Props) => {
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const onAfterScroll = useCallback(
    (oldLastSeen: number, newLastSeen: number) => dispatch(wishlistAfterScroll(oldLastSeen, newLastSeen)),
    [],
  );

  const rows = tableRef.getCoreRowModel().rows;
  const hasItems = rows.length > 0;
  const entryIds = rows.map((row) => row.original.id);

  const canAddToCart = canSeeAddToCartButton();
  const isSomethingSelectable = rows.some((row) => row.getCanSelect());

  const listRef = useListVisibilityWatcher<HTMLDivElement>(onAfterScroll, ".js-wishlist-entry", entryIds);

  const isAnySelected = tableRef.getIsSomeRowsSelected();
  const isAllSelected = tableRef.getIsAllRowsSelected();

  const canSelectItems = canAddToCart || userIsRegistered();

  const selectAllCbox = {
    checked: isAllSelected,
    disabled: !canSelectItems || !hasItems || !isSomethingSelectable,
    indeterminate: !isAllSelected && isAnySelected,
    onCheck: tableRef.toggleAllRowsSelected,
  };

  const [quickEntryModal, openQuickEntry] = useWishlistQuickEntry();

  if (!hasItems) {
    return (
      <>
        <EmptyWishlist className={className} onCtaClick={openQuickEntry} />
        {quickEntryModal}
      </>
    );
  }

  const quickEntryButton = (
    <LinkButton onClick={openQuickEntry} iconBefore={<Icon type={IconType.Plus} />} className="mt-2">
      {t("page.wishlist.button.quickEntry")}
    </LinkButton>
  );

  return (
    <div className={classNames(className, classes.root, isCustomer && classes.border)}>
      {quickEntryModal}
      <div className={classes.header}>
        {quickEntryButton}
        <Checkbox className={classes.checkbox} data-test-id={"wishlist-cbox-select-all"} {...selectAllCbox} />
      </div>

      {hasItems && (
        <div ref={listRef} className={classes.list}>
          {rows.map((row, index) => (
            <WishlistItem
              key={row.original.id}
              entryId={row.original.id}
              index={index}
              row={row}
              canAddToCart={canAddToCart}
              setSaleEnabled={makeSetSaleEnabled(index)}
            />
          ))}
        </div>
      )}
      {rows.length > 3 && quickEntryButton}
    </div>
  );
};

const EmptyWishlist = ({ onCtaClick, className }: { onCtaClick: () => void; className?: string }) => {
  const { t } = useTranslation();
  return (
    <InformationPage.CenterContents className={classNames("pb-30", className)}>
      <InformationPage.Title>{t("page.wishlist.empty.title")}</InformationPage.Title>
      <InformationPage.Image src={IMG_EMPTY_WISHLIST} />
      <InformationPage.CtaButton onClick={onCtaClick}>{t("page.wishlist.button.quickEntry")}</InformationPage.CtaButton>
      <InformationPage.GoHomepageLink />
    </InformationPage.CenterContents>
  );
};
